import { useEffect, useState } from "react";

import { Anchor, Button } from "@newsela/angelou";
import { actionNames, actionPrefixes, withTrackEvent } from "@newsela/snitch";

import { CHIRON_CONTENT_PROMOTION_PATHNAME } from "@/components/ContentPromotions/constants";
import { QuestionType } from "@/components/ContentPromotions/types";
import { ContentPromotionContentSource } from "@/components/ContentPromotions/types/ContentPromotionQuestionResponseSource.enum";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import evaluateRestQuery from "@/remoteState/evaluateRestQuery";
import { ContentPromotionFormResponse } from "@/types/models/ContentPromotionFormResponse.interface";
import stringifyParams from "@/utils/stringifyParams";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";

import { EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API } from "@/search/Langston/constants";
import TopicCard from "./TopicCard";
import LoadingRow from "./LoadingRow";
import CustomResourceLink from "./CustomResourceLink";
import ContentTypeCard from "./ContentTypeCard";
import CustomLinkBG from "./images/CustomLinkBG.svg?component";

import {
  customResourcesCss,
  backgroundImageCss,
  contentContainerCss,
  rowContainerCss,
  rowTitleCss,
  editButtonCss,
  rowTitleContainerCss,
} from "./ContentPreviewRow.style";
import MonolithContentCard from "./MonolithContentCard";
import ReadingSkillsCard from "./ReadingSkillsCard";

export interface PreviewRowContent {
  responses: ContentPromotionFormResponse["response"];
  contentSource: ContentPromotionContentSource;
  formId: string;
  questionNumber: number;
  title: string;
}
interface PreviewRowProps {
  rowContent: PreviewRowContent;
  showEditButton?: boolean;
}

export interface ContentProps {
  title: string;
  href: string;
  image_url?: string | null;
}

type AnchorProps = React.ComponentProps<typeof Anchor>;

const AnchorWithTrackEvent = withTrackEvent(
  Anchor,
) as React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
>;

const fetchMonolithCardContent = async (rowContent: PreviewRowContent) => {
  const contentIds = rowContent.responses.map(
    (response) => response.content_id,
  );

  const params = {
    depth: 1,
    ids: contentIds.join(","),
    include_rels: "children",
  };

  const queryParamString = stringifyParams(params);
  const contentEndpoint = `${MONOLITH_ORIGIN}/api/v3/content`;

  const fullContentUrl = contentEndpoint + queryParamString;
  const contentRequest = new Request(fullContentUrl);
  const { data } = await evaluateRestQuery<ContentProps[]>(contentRequest);
  return data ?? [];
};

const generateReadingSkillsCardContent = (
  rowContent: PreviewRowContent,
  baseSearchHref: string,
  isLangstonSearchOn: boolean,
): ContentProps[] => {
  const isReadingSkillsRow =
    rowContent.title === QuestionType.ela_standards_and_skills;

  return rowContent.responses.map((response) => {
    let href: string;

    if (isLangstonSearchOn) {
      href = isReadingSkillsRow
        ? `${baseSearchHref}&reading_skills=${response.content_id ?? ""}`
        : `${baseSearchHref}&topics=${response.content_id ?? ""}`;
    } else {
      // Otherwise, use the original logic.
      href = isReadingSkillsRow
        ? `${baseSearchHref}&reading_skills=${response.slug ?? ""}`
        : `${baseSearchHref}&taxonomy_slug=${response.slug ?? ""}`;
    }

    return {
      title: response.name ?? response.text,
      href,
      image_url: response.image_url,
    };
  });
};

const generateCustomResourceLinks = (
  rowContent: PreviewRowContent,
): ContentProps[] => {
  return rowContent.responses.map((response) => {
    return {
      href: response.url ?? "",
      title: response.text ?? "",
    };
  });
};

const generateTopicCards = (
  rowContent: PreviewRowContent,
  baseSearchHref: string,
  isLangstonSearchOn: boolean,
): ContentProps[] => {
  return rowContent.responses.map((response) => {
    const searchParamKey = isLangstonSearchOn ? "topics" : "taxonomy_slug";

    const searchParamValue = isLangstonSearchOn
      ? response.content_id
      : response.slug;

    return {
      href: `${baseSearchHref}&${searchParamKey}=${searchParamValue ?? ""}`,
      image_url: response.image_url,
      title: response.text ?? "",
    };
  });
};

const ContentPreviewRow = ({ rowContent, showEditButton }: PreviewRowProps) => {
  const isReadingSkillsRow =
    rowContent.title === QuestionType.ela_standards_and_skills;

  const [content, setContent] = useState<ContentProps[]>();

  const isTopicRow =
    rowContent.title === QuestionType.topics ||
    rowContent.title === QuestionType.topics_and_themes;

  const [langstonSearchExperiment] = useGetUserExperimentSegment(
    EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API,
  );

  const isLangstonSearchOn = langstonSearchExperiment === "on";
  const getCorrectContentType = () =>
    isLangstonSearchOn ? "ARTICLE" : "articleheader";

  const baseSearchHref = `/search/?content_types=${getCorrectContentType()}`;

  const getPreviewContent = async (): Promise<ContentProps[]> => {
    if (rowContent?.responses && rowContent.responses?.length > 0) {
      if (
        isReadingSkillsRow ||
        rowContent.contentSource === ContentPromotionContentSource.Langston
      ) {
        return generateReadingSkillsCardContent(
          rowContent,
          baseSearchHref,
          isLangstonSearchOn,
        );
      }

      if (rowContent.title === QuestionType.custom_resources) {
        return generateCustomResourceLinks(rowContent);
      }

      if (isTopicRow) {
        return generateTopicCards(
          rowContent,
          baseSearchHref,
          isLangstonSearchOn,
        );
      }

      return await fetchMonolithCardContent(rowContent);
    }
    return [];
  };

  useEffect(() => {
    void getPreviewContent().then((contentFromApi) => {
      setContent(contentFromApi);
    });
  }, []);

  const eventProperties = {
    objectType: "Platfom Alignment Form Content",
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      description: `Redirect to edit ${rowContent?.title}`,
    },
  };

  if (!content && !isReadingSkillsRow) {
    return <LoadingRow numberOfCards={rowContent.responses?.length} />;
  }

  if (content?.length === 0 && !isReadingSkillsRow) {
    return <></>;
  }

  const renderContent = (content: ContentProps, index: number) => {
    if (isTopicRow) {
      return <TopicCard {...content} />;
    }

    if (isReadingSkillsRow) {
      return <ReadingSkillsCard cardIndex={index} {...content} />;
    }

    if (rowContent.contentSource === ContentPromotionContentSource.Monolith) {
      return <MonolithContentCard {...content} />;
    }

    if (rowContent.title === QuestionType.custom_resources) {
      return <CustomResourceLink {...content} />;
    }

    if (rowContent.title === QuestionType.content_types) {
      return <ContentTypeCard cardIndex={index} {...content} />;
    }

    return <ReadingSkillsCard cardIndex={index} {...content} />;
  };

  const isCustomResources = rowContent.title === QuestionType.custom_resources;

  const defaultOrCustomLinkCss = isCustomResources
    ? customResourcesCss
    : contentContainerCss;

  return (
    <div css={rowContainerCss}>
      <div css={rowTitleContainerCss}>
        <div css={rowTitleCss}>{rowContent.title}</div>
        {showEditButton && (
          <AnchorWithTrackEvent
            dataProps={{
              "data-qa-selector": `edit-${rowContent?.title}-button`,
            }}
            styleAnchorAsButton={{
              size: Button.sizes.md,
            }}
            __cssFor={{
              root: editButtonCss,
            }}
            href={`${CHIRON_CONTENT_PROMOTION_PATHNAME}/form/${rowContent.formId}/?question_number=${rowContent.questionNumber}`}
            {...eventProperties}
          >
            Edit {(rowContent?.title).toLocaleLowerCase()}
          </AnchorWithTrackEvent>
        )}
      </div>
      <ul css={defaultOrCustomLinkCss}>
        {content?.map((pieceOfContent, index) => (
          <li key={index}>{renderContent(pieceOfContent, index)}</li>
        ))}
      </ul>
      {isCustomResources && (
        <div css={backgroundImageCss}>
          <CustomLinkBG />
        </div>
      )}
    </div>
  );
};

export default ContentPreviewRow;
